/**
 * Find work — requests from businesses that builders can estimate on: open
 * public ones and the ones this builder was invited to (never their own
 * organization's). Search, a kind filter and an Invited tab, all answered by
 * the server (GET /build/work). Doc: docs/marketplace_get_it_built.md
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { money, mktError } from '@/api/marketplace'
import {
  findWork, getBuilderDashboard, KIND_LABELS, type BuildRequest, type BuilderDashboard, type RequestKind,
} from '@/api/marketplaceBuild'
import MarketplaceShell from '../MarketplaceShell'
import { Avatar, budgetText, EligibleBanner, KindPill, QuotePill, relTime } from './builderUi'

const KINDS = Object.keys(KIND_LABELS) as RequestKind[]

export default function FindWorkPage() {
  const [params, setParams] = useSearchParams()
  const q = params.get('q') || ''
  const kindParam = params.get('kind') || ''
  const kind = (KINDS as string[]).includes(kindParam) ? (kindParam as RequestKind) : ''
  const invited = params.get('tab') === 'invited'

  const [text, setText] = useState(q)
  const [requests, setRequests] = useState<BuildRequest[] | null>(null)
  const [eligible, setEligible] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => setText(q), [q])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await findWork({ q: q || undefined, kind: kind || undefined, invited: invited || undefined })
      setRequests(r.requests)
      setEligible(r.eligible)
    } catch (e) {
      setError(mktError(e, "We couldn't load the requests.").message)
    } finally {
      setLoading(false)
    }
  }, [q, kind, invited])
  useEffect(() => { load() }, [load])

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value); else next.delete(key)
    setParams(next, { replace: true })
  }
  const onSearch = (e: FormEvent) => { e.preventDefault(); setParam('q', text.trim()) }
  const clear = () => { setText(''); setParams(new URLSearchParams(invited ? { tab: 'invited' } : {}), { replace: true }) }

  return (
    <MarketplaceShell>
      <div className="mkt-wrap mkt-col" style={{ padding: '24px 20px 56px', gap: 16 }}>
        <div className="mkt-row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div className="mkt-col" style={{ gap: 4, maxWidth: 760 }}>
            <h1 style={{ fontSize: 30 }}>Find work</h1>
            <p className="mkt-muted" style={{ margin: 0 }}>Businesses asking for apps. You build in your own Twasta account; when the client accepts it, it’s installed for them and they pay you every month to use it.</p>
          </div>
        </div>

        <EligibleBanner eligible={eligible} />

        <form onSubmit={onSearch} className="mkt-search" style={{ maxWidth: 'none' }} role="search">
          <label htmlFor="fw-q" className="mkt-sr">Search requests</label>
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ color: 'var(--color-text-faint)' }}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          <input id="fw-q" value={text} onChange={(e) => setText(e.target.value)} placeholder="Search by what needs building — e.g. inventory, dashboards, approvals" />
          <button type="submit" className="btn btn-primary">Search</button>
        </form>

        <div role="tablist" aria-label="Request lists" className="mkt-tabs">
          <button type="button" role="tab" aria-selected={!invited} className={`mkt-tab${!invited ? ' on' : ''}`} onClick={() => setParam('tab', '')}>All open requests</button>
          <button type="button" role="tab" aria-selected={invited} className={`mkt-tab${invited ? ' on' : ''}`} onClick={() => setParam('tab', 'invited')}>Invited</button>
        </div>

        <div className="mkt-split">
          <div className="mkt-main" style={{ gap: 12 }}>
            <div className="mkt-chips" role="group" aria-label="Kind of work">
              <button type="button" className={`mkt-chip${!kind ? ' on' : ''}`} aria-pressed={!kind} onClick={() => setParam('kind', '')}>Every kind</button>
              {KINDS.map((k) => (
                <button key={k} type="button" className={`mkt-chip${kind === k ? ' on' : ''}`} aria-pressed={kind === k} onClick={() => setParam('kind', kind === k ? '' : k)}>{KIND_LABELS[k]}</button>
              ))}
            </div>

            {error && (
              <div role="alert" className="mkt-banner bad"><div><strong>Something went wrong</strong>{error}</div><button type="button" className="btn btn-secondary" onClick={load}>Try again</button></div>
            )}

            {requests && !error && (
              <span className="mkt-muted" style={{ fontSize: 14 }} aria-live="polite">
                <strong style={{ color: 'var(--color-text)' }}>{requests.length}</strong> request{requests.length === 1 ? '' : 's'} {q || kind ? 'match' : invited ? 'you were invited to' : 'open now'}
                {loading && ' · updating…'}
              </span>
            )}
            {!requests && !error && <span className="mkt-muted" role="status">Loading requests…</span>}

            {requests?.map((r) => <RequestCard key={r.id} r={r} />)}

            {requests && requests.length === 0 && !error && (
              <div className="mkt-empty">
                <strong style={{ fontSize: 18 }}>{invited ? 'No invitations right now' : 'No requests match'}</strong>
                <span className="mkt-muted">{invited ? 'When a business invites you to estimate, it shows up here.' : 'Try other words or another kind of work.'}</span>
                {(q || kind) && <button type="button" className="btn btn-secondary" onClick={clear}>Clear the search</button>}
              </div>
            )}
          </div>

          <BuilderAside />
        </div>
      </div>
    </MarketplaceShell>
  )
}

function RequestCard({ r }: { r: BuildRequest }) {
  const items = (r.brief || []).flatMap((s) => s.items || [])
  const shown = items.length ? items : r.feature_labels || []
  const more = shown.length > 8 ? shown.length - 8 : 0
  const c = r.client
  return (
    <article className="mkt-panel mkt-col" style={{ gap: 12, padding: 20 }}>
      <div className="mkt-row" style={{ gap: 6 }}>
        {r.posted_at && <span className="mkt-faint">Posted {relTime(r.posted_at)}</span>}
        <KindPill kind={r.kind} label={r.kind_label} />
        {r.invited_you && <span className="mkt-pill warn">You were invited</span>}
        {r.my_quote && <><QuotePill status={r.my_quote.status} />{r.my_quote.version > 1 && <span className="mkt-faint">version {r.my_quote.version}</span>}</>}
      </div>
      <Link to={`/marketplace/work/${r.id}`} style={{ fontSize: 19, fontWeight: 700, color: 'var(--color-text)' }}>{r.title || 'Untitled request'}</Link>
      <div className="mkt-row" style={{ gap: '8px 20px', fontSize: 14 }}>
        <span><span className="mkt-faint">Budget</span> <strong>{budgetText(r)}</strong></span>
        <span><span className="mkt-faint">Pays</span> <strong>Fixed price, standard stages</strong></span>
        {r.timeline && <span><span className="mkt-faint">Needed</span> <strong>{r.timeline}</strong></span>}
      </div>
      {r.description && (
        <p className="mkt-muted" style={{ margin: 0, fontSize: 14, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r.description}</p>
      )}
      {shown.length > 0 && (
        <div className="mkt-row" style={{ gap: 6 }} aria-label="What the brief asks for">
          {shown.slice(0, 8).map((t, i) => <span key={`${t}-${i}`} className="mkt-pill info">{t}</span>)}
          {more > 0 && <span className="mkt-faint">+{more} more</span>}
        </div>
      )}
      <div className="mkt-row" style={{ gap: '8px 18px', fontSize: 13, borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
        {c?.payment_verified
          ? <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>✓ Payment verified</span>
          : <span className="mkt-muted">Payment not verified yet</span>}
        {c && <span className="mkt-muted">{c.orders ? `Hired on Twasta ${c.orders} time${c.orders === 1 ? '' : 's'} · ${c.completed} finished` : 'New client'}</span>}
        {c && c.spent_minor > 0 && <span className="mkt-muted">{money(c.spent_minor, r.currency)} spent on Twasta</span>}
        {r.closes_at && <span className="mkt-muted">Closes {relTime(r.closes_at)}</span>}
        <span className="mkt-spacer" />
        <span className="mkt-muted">Estimates: <strong style={{ color: 'var(--color-text)' }}>{r.estimates_count}</strong></span>
        <Link to={`/marketplace/work/${r.id}`} className="btn btn-primary btn-sm">{r.my_quote ? 'Open your estimate' : 'View & estimate'}</Link>
      </div>
    </article>
  )
}

/** "Your work right now" — read from the builder dashboard; optional, so a failure only hides it. */
function BuilderAside() {
  const [d, setD] = useState<BuilderDashboard | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let live = true
    getBuilderDashboard().then((x) => { if (live) setD(x) }).catch(() => { if (live) setFailed(true) })
    return () => { live = false }
  }, [])
  if (failed) return null
  if (!d) return <aside className="mkt-aside"><div className="mkt-panel"><span className="mkt-muted" role="status">Loading your work…</span></div></aside>
  const cur = d.earnings[0]?.currency || d.orders[0]?.currency || 'INR'
  const held = d.orders.reduce((a, o) => a + (o.currency === cur ? o.money.held_minor : 0), 0)
  const active = d.orders.filter((o) => o.status === 'in_progress' || o.status === 'placed').length
  const waiting = d.estimates.filter((e) => e.status === 'sent' || e.status === 'revision_asked').length
  const monthly = d.client_apps.filter((a) => a.currency === cur).reduce((a, c) => a + (c.last_charge?.earn_minor || 0), 0)
  const p = d.profile
  return (
    <aside className="mkt-aside" aria-label="Your work">
      {p && (
        <div className="mkt-panel mkt-col" style={{ gap: 10, padding: 18 }}>
          <div className="mkt-row" style={{ flexWrap: 'nowrap' }}>
            <Avatar text={p.initials} size={48} available={p.available} />
            <div className="mkt-col" style={{ gap: 0 }}>
              <strong>{p.name}</strong>
              <span className="mkt-faint">{d.stats.rating != null ? `★ ${d.stats.rating} · ` : ''}{d.stats.completed} finished</span>
            </div>
          </div>
          <span className={`mkt-pill ${p.available ? 'ok' : ''}`} style={{ alignSelf: 'flex-start' }}>{p.available ? '● Available for new work' : 'Not taking new work'}</span>
          <div className="mkt-row" style={{ gap: 8 }}>
            <Link to={`/marketplace/builders/${encodeURIComponent(p.tenant_id)}`} className="btn btn-ghost btn-sm">Your public profile</Link>
            <Link to="/marketplace/builder?tab=profile" className="btn btn-secondary btn-sm">Change</Link>
          </div>
        </div>
      )}
      <div className="mkt-panel mkt-col" style={{ gap: 8, padding: 18, fontSize: 14 }}>
        <strong>Your work right now</strong>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Being built</span><Link to="/marketplace/builder?tab=orders">{active}</Link></div>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Estimates waiting</span><Link to="/marketplace/builder?tab=estimates">{waiting}</Link></div>
        <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Held by Twasta for you</span><strong>{money(held, cur)}</strong></div>
        {d.client_apps.length > 0 && <div className="mkt-row" style={{ justifyContent: 'space-between' }}><span className="mkt-muted">Latest monthly earnings from client apps</span><strong>{money(monthly, cur)}</strong></div>}
      </div>
      <div className="mkt-panel mkt-col" style={{ gap: 8, padding: 18 }}>
        <strong>Win more work</strong>
        <ul className="mkt-muted" style={{ margin: 0, paddingLeft: 18, fontSize: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <li>Answer within a few hours — clients often hire the first good answer.</li>
          <li>Link a live demo of a similar app.</li>
          <li>Cover every brief item, or say plainly what you leave out.</li>
        </ul>
      </div>
    </aside>
  )
}
